const ArgumentType = require('../../extension-support/argument-type');
const BlockType = require('../../extension-support/block-type');
const Cast = require('../../util/cast');
const Video = require('../../io/video');
const StageLayering = require('../../engine/stage-layering');

const LABEL = BlockType.LABEL || 'label';

const menuIconURI = '';
const blockIconURI = '';

const COCO_OBJECTS = [
    'any',
    'person',
    'bicycle',
    'car',
    'motorcycle',
    'airplane',
    'bus',
    'train',
    'truck',
    'boat',
    'traffic light',
    'fire hydrant',
    'stop sign',
    'parking meter',
    'bench',
    'bird',
    'cat',
    'dog',
    'horse',
    'sheep',
    'cow',
    'elephant',
    'bear',
    'zebra',
    'giraffe',
    'backpack',
    'umbrella',
    'handbag',
    'tie',
    'suitcase',
    'frisbee',
    'skis',
    'snowboard',
    'sports ball',
    'kite',
    'baseball bat',
    'baseball glove',
    'skateboard',
    'surfboard',
    'tennis racket',
    'bottle',
    'wine glass',
    'cup',
    'fork',
    'knife',
    'spoon',
    'bowl',
    'banana',
    'apple',
    'sandwich',
    'orange',
    'broccoli',
    'carrot',
    'hot dog',
    'pizza',
    'donut',
    'cake',
    'chair',
    'couch',
    'potted plant',
    'bed',
    'dining table',
    'toilet',
    'tv',
    'laptop',
    'mouse',
    'remote',
    'keyboard',
    'cell phone',
    'microwave',
    'oven',
    'toaster',
    'sink',
    'refrigerator',
    'book',
    'clock',
    'vase',
    'scissors',
    'teddy bear',
    'hair drier',
    'toothbrush'
];

class Scratch3MadBotObjectAI {
    constructor (runtime) {
        this.runtime = runtime;

        this.model = null;
        this.modelPromise = null;

        this.videoOn = false;
        this.detectionOn = false;
        this.status = 'idle';

        this.cameraSource = 'front';
        this.detectionSource = 'camera';

        this.mirror = true;
        this.transparency = 0;

        this.minScore = 0.5;
        this.maxBoxes = 10;
        this.detectInterval = 140;

        this.predictions = [];

        this.showBox = true;
        this.showLabel = true;
        this.showScore = true;

        this.loopTimer = null;
        this.loopBusy = false;

        this.overlayCanvas = null;
        this.overlayCtx = null;
        this.overlaySkinId = -1;
        this.overlayDrawable = -1;

        this.stageCanvas = null;
        this.stageCtx = null;
    }

    getInfo () {
        return {
            id: 'madbotObjectAI',
            name: 'MadBot Object AI',
            color1: '#F88A17',
            color2: '#D96D00',
            color3: '#B95400',
            menuIconURI,
            blockIconURI,
            blocks: [
                {
                    blockType: LABEL,
                    text: 'Control'
                },
                {
                    opcode: 'controlObjectAI',
                    blockType: BlockType.COMMAND,
                    text: 'object AI [ACTION] source [SOURCE] camera [CAMERA] transparency [TRANSPARENCY]',
                    arguments: {
                        ACTION: {
                            type: ArgumentType.STRING,
                            menu: 'ACTION',
                            defaultValue: 'camera on'
                        },
                        SOURCE: {
                            type: ArgumentType.STRING,
                            menu: 'SOURCE',
                            defaultValue: 'camera'
                        },
                        CAMERA: {
                            type: ArgumentType.STRING,
                            menu: 'CAMERA',
                            defaultValue: 'front'
                        },
                        TRANSPARENCY: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 0
                        }
                    }
                },

                {
                    blockType: LABEL,
                    text: 'Settings'
                },
                {
                    opcode: 'setConfidence',
                    blockType: BlockType.COMMAND,
                    text: 'set object confidence to [CONFIDENCE] %',
                    arguments: {
                        CONFIDENCE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 50
                        }
                    }
                },
                {
                    opcode: 'setMaxDetections',
                    blockType: BlockType.COMMAND,
                    text: 'set max detections to [MAX]',
                    arguments: {
                        MAX: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 10
                        }
                    }
                },

                {
                    blockType: LABEL,
                    text: 'Overlay'
                },
                {
                    opcode: 'showOverlay',
                    blockType: BlockType.COMMAND,
                    text: 'show object [PART] [STATE]',
                    arguments: {
                        PART: {
                            type: ArgumentType.STRING,
                            menu: 'OVERLAY_PART',
                            defaultValue: 'box'
                        },
                        STATE: {
                            type: ArgumentType.STRING,
                            menu: 'ON_OFF',
                            defaultValue: 'on'
                        }
                    }
                },

                {
                    blockType: LABEL,
                    text: 'Object Reports'
                },
                {
                    opcode: 'objectsDetected',
                    blockType: BlockType.REPORTER,
                    text: 'objects detected'
                },
                {
                    opcode: 'isObjectDetected',
                    blockType: BlockType.BOOLEAN,
                    text: 'is object [OBJECT] detected?',
                    arguments: {
                        OBJECT: {
                            type: ArgumentType.STRING,
                            menu: 'OBJECTS',
                            defaultValue: 'person'
                        }
                    }
                },
                {
                    opcode: 'countObject',
                    blockType: BlockType.REPORTER,
                    text: 'count of object [OBJECT]',
                    arguments: {
                        OBJECT: {
                            type: ArgumentType.STRING,
                            menu: 'OBJECTS',
                            defaultValue: 'person'
                        }
                    }
                },
                {
                    opcode: 'firstObject',
                    blockType: BlockType.REPORTER,
                    text: 'first detected object'
                },
                {
                    opcode: 'objectValue',
                    blockType: BlockType.REPORTER,
                    text: '[VALUE] of object [OBJECT]',
                    arguments: {
                        VALUE: {
                            type: ArgumentType.STRING,
                            menu: 'OBJECT_VALUE',
                            defaultValue: 'center x'
                        },
                        OBJECT: {
                            type: ArgumentType.STRING,
                            menu: 'OBJECTS',
                            defaultValue: 'person'
                        }
                    }
                },
                {
                    opcode: 'allObjects',
                    blockType: BlockType.REPORTER,
                    text: 'all detected objects'
                },
                {
                    opcode: 'detectableObjects',
                    blockType: BlockType.REPORTER,
                    text: 'detectable objects list'
                },
                {
                    opcode: 'statusReporter',
                    blockType: BlockType.REPORTER,
                    text: 'object detection status'
                },
                {
                    opcode: 'sourceReporter',
                    blockType: BlockType.REPORTER,
                    text: 'object detection source'
                },

                {
                    blockType: LABEL,
                    text: 'Events'
                },
                {
                    opcode: 'whenAnyObjectDetected',
                    blockType: BlockType.HAT,
                    text: 'when any object detected'
                },
                {
                    opcode: 'whenObjectDetected',
                    blockType: BlockType.HAT,
                    text: 'when object [OBJECT] detected',
                    arguments: {
                        OBJECT: {
                            type: ArgumentType.STRING,
                            menu: 'OBJECTS',
                            defaultValue: 'person'
                        }
                    }
                }
            ],
            menus: {
                ACTION: {
                    acceptReporters: true,
                    items: [
                        {text: 'camera on', value: 'camera on'},
                        {text: 'camera off', value: 'camera off'},
                        {text: 'detection start', value: 'detection start'},
                        {text: 'detection stop', value: 'detection stop'}
                    ]
                },
                SOURCE: {
                    acceptReporters: true,
                    items: [
                        {text: 'camera', value: 'camera'},
                        {text: 'stage', value: 'stage'}
                    ]
                },
                CAMERA: {
                    acceptReporters: true,
                    items: [
                        {text: 'front', value: 'front'},
                        {text: 'back', value: 'back'}
                    ]
                },
                OBJECTS: {
                    acceptReporters: true,
                    items: COCO_OBJECTS.map(objectName => ({
                        text: objectName,
                        value: objectName
                    }))
                },
                OVERLAY_PART: {
                    acceptReporters: true,
                    items: [
                        {text: 'box', value: 'box'},
                        {text: 'label', value: 'label'},
                        {text: 'score', value: 'score'}
                    ]
                },
                ON_OFF: {
                    acceptReporters: true,
                    items: [
                        {text: 'on', value: 'on'},
                        {text: 'off', value: 'off'}
                    ]
                },
                OBJECT_VALUE: {
                    acceptReporters: true,
                    items: [
                        {text: 'x', value: 'x'},
                        {text: 'y', value: 'y'},
                        {text: 'center x', value: 'center x'},
                        {text: 'center y', value: 'center y'},
                        {text: 'width', value: 'width'},
                        {text: 'height', value: 'height'},
                        {text: 'confidence', value: 'confidence'}
                    ]
                }
            }
        };
    }

    async controlObjectAI (args) {
        const action = Cast.toString(args.ACTION || 'camera on');
        const source = Cast.toString(args.SOURCE || 'camera');
        const camera = Cast.toString(args.CAMERA || 'front');
        const transparency = Cast.toNumber(args.TRANSPARENCY);

        this.cameraSource = camera === 'back' ? 'back' : 'front';
        this.transparency = this._clamp(transparency, 0, 100);
        this.mirror = this.cameraSource !== 'back';

        if (action === 'camera on') {
            await this._turnOnCamera();
            return;
        }

        if (action === 'camera off') {
            this._turnOffCamera();
            return;
        }

        if (action === 'detection start') {
            this.detectionSource = source === 'stage' ? 'stage' : 'camera';
            await this._startDetection();
            return;
        }

        if (action === 'detection stop') {
            this._stopDetection();
        }
    }

    async _turnOnCamera () {
        this.status = 'starting camera';

        try {
            await this._enableScratchCamera();
            this.videoOn = true;
            this.status = 'camera ready';
        } catch (error) {
            console.warn('MadBot Object AI camera error:', error);
            this.status = `camera error: ${error.message || error}`;
        }
    }

    _turnOffCamera () {
        if (this.detectionSource === 'camera') {
            this._stopDetection();
        }

        const videoDevice = this._videoDevice();

        if (videoDevice) {
            try {
                videoDevice.disableVideo();
            } catch (error) {
                console.warn('MadBot Object AI camera off error:', error);
            }
        }

        this.videoOn = false;

        if (!this.detectionOn) {
            this.predictions = [];
            this._clearOverlay();
            this.status = 'camera off';
        }
    }

    async _startDetection () {
        if (this.detectionSource === 'camera' && !this.videoOn) {
            await this._turnOnCamera();
        }

        if (this.detectionSource === 'camera' && !this.videoOn) {
            return;
        }

        try {
            this.status = 'loading model';
            await this._ensureModel();

            this.detectionOn = true;
            this.status = `detecting from ${this.detectionSource}`;
            this._scheduleLoop();
        } catch (error) {
            console.warn('MadBot Object AI model error:', error);
            this.status = `model error: ${error.message || error}`;
        }
    }

    _stopDetection () {
        this.detectionOn = false;

        if (this.loopTimer) {
            clearTimeout(this.loopTimer);
            this.loopTimer = null;
        }

        this.predictions = [];
        this._clearOverlay();

        if (this.videoOn) {
            this.status = 'camera ready';
        } else {
            this.status = 'idle';
        }
    }

    setConfidence (args) {
        const value = this._clamp(Cast.toNumber(args.CONFIDENCE), 1, 100);
        this.minScore = value / 100;
    }

    setMaxDetections (args) {
        this.maxBoxes = Math.round(this._clamp(Cast.toNumber(args.MAX), 1, 20));
    }

    showOverlay (args) {
        const part = Cast.toString(args.PART);
        const state = Cast.toString(args.STATE) === 'on';

        if (part === 'box') this.showBox = state;
        if (part === 'label') this.showLabel = state;
        if (part === 'score') this.showScore = state;

        this._drawOverlay();
    }

    objectsDetected () {
        return this.predictions.length;
    }

    isObjectDetected (args) {
        return Boolean(this._findPrediction(Cast.toString(args.OBJECT)));
    }

    countObject (args) {
        const name = Cast.toString(args.OBJECT).trim().toLowerCase();

        if (!name || name === 'any') {
            return this.predictions.length;
        }

        return this.predictions.filter(item =>
            item.class.toLowerCase() === name
        ).length;
    }

    firstObject () {
        if (!this.predictions.length) return '';
        return this.predictions[0].class;
    }

    objectValue (args) {
        const value = Cast.toString(args.VALUE);
        const objectName = Cast.toString(args.OBJECT);

        const item = this._findPrediction(objectName);
        if (!item) return 0;

        const box = item.bbox;
        const x = box[0];
        const y = box[1];
        const width = box[2];
        const height = box[3];

        const centerX = x + (width / 2);
        const centerY = y + (height / 2);

        if (value === 'x') return Math.round(x - 240);
        if (value === 'y') return Math.round(180 - y);
        if (value === 'center x') return Math.round(centerX - 240);
        if (value === 'center y') return Math.round(180 - centerY);
        if (value === 'width') return Math.round(width);
        if (value === 'height') return Math.round(height);
        if (value === 'confidence') return Math.round(item.score * 100);

        return 0;
    }

    allObjects () {
        if (!this.predictions.length) return '';
        return this.predictions.map(item => item.class).join(', ');
    }

    detectableObjects () {
        return COCO_OBJECTS.join(', ');
    }

    statusReporter () {
        return this.status;
    }

    sourceReporter () {
        return this.detectionSource;
    }

    whenAnyObjectDetected () {
        return this.predictions.length > 0;
    }

    whenObjectDetected (args) {
        return this.isObjectDetected(args);
    }

    _videoDevice () {
        if (!this.runtime || !this.runtime.ioDevices) return null;
        return this.runtime.ioDevices.video || null;
    }

    async _enableScratchCamera () {
        const videoDevice = this._videoDevice();

        if (!videoDevice) {
            throw new Error('Scratch video device not found');
        }

        await this._replaceScratchCameraStream();

        const result = videoDevice.enableVideo();

        if (result && typeof result.then === 'function') {
            await result;
        }

        videoDevice.mirror = this.mirror;

        if (videoDevice.setPreviewGhost) {
            videoDevice.setPreviewGhost(this.transparency);
        }
    }

    async _replaceScratchCameraStream () {
        const videoDevice = this._videoDevice();

        if (!videoDevice || !videoDevice.provider) return;

        const provider = videoDevice.provider;

        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            return;
        }

        const facingMode = this.cameraSource === 'back' ? 'environment' : 'user';

        if (
            provider.__madbotObjectFacingMode === facingMode &&
            provider.videoReady
        ) {
            return;
        }

        let stream = null;
        let lastError = null;

        const constraintsList = [
            {
                audio: false,
                video: {
                    width: {min: 480, ideal: 640},
                    height: {min: 360, ideal: 480},
                    facingMode: {ideal: facingMode}
                }
            },
            {
                audio: false,
                video: {
                    width: {ideal: 640},
                    height: {ideal: 480},
                    facingMode
                }
            },
            {
                audio: false,
                video: true
            }
        ];

        for (const constraints of constraintsList) {
            try {
                stream = await navigator.mediaDevices.getUserMedia(constraints);
                break;
            } catch (error) {
                lastError = error;
            }
        }

        if (!stream) {
            throw lastError || new Error('camera not available');
        }

        try {
            if (provider._track) {
                provider._track.stop();
            }
        } catch (error) {
            // Ignore old camera track stop error.
        }

        const video = document.createElement('video');
        video.setAttribute('playsinline', 'true');
        video.setAttribute('webkit-playsinline', 'true');
        video.autoplay = true;
        video.muted = true;

        try {
            video.srcObject = stream;
        } catch (error) {
            video.src = window.URL.createObjectURL(stream);
        }

        try {
            await video.play();
        } catch (error) {
            // Some browsers allow video after metadata is ready.
        }

        provider.enabled = true;
        provider._video = video;
        provider._track = stream.getVideoTracks()[0];
        provider._workspace = [];
        provider._singleSetup = Promise.resolve(provider);
        provider.__madbotObjectFacingMode = facingMode;

        this.mirror = this.cameraSource !== 'back';
        videoDevice.mirror = this.mirror;
    }

    async _ensureModel () {
        if (this.model) return this.model;
        if (this.modelPromise) return this.modelPromise;

        this.modelPromise = this._loadModel();
        return this.modelPromise;
    }

    async _loadModel () {
        if (typeof window === 'undefined') {
            throw new Error('browser window not found');
        }

        this.status = 'loading TensorFlow';

        if (!window.tf) {
            await this._loadScriptOnce(
                'madbot-tfjs',
                'https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.22.0/dist/tf.min.js',
                () => window.tf
            );
        }

        if (window.tf && window.tf.ready) {
            await window.tf.ready();
        }

        this.status = 'loading COCO-SSD';

        if (!window.cocoSsd) {
            await this._loadScriptOnce(
                'madbot-coco-ssd',
                'https://cdn.jsdelivr.net/npm/@tensorflow-models/coco-ssd@2.2.3',
                () => window.cocoSsd
            );
        }

        if (!window.cocoSsd) {
            throw new Error('COCO-SSD not loaded');
        }

        this.model = await window.cocoSsd.load({
            base: 'lite_mobilenet_v2'
        });

        this.status = 'model ready';
        return this.model;
    }

    _loadScriptOnce (key, src, testFn) {
        if (testFn && testFn()) {
            return Promise.resolve();
        }

        window.__madbotAILoaders = window.__madbotAILoaders || {};

        if (window.__madbotAILoaders[key]) {
            return window.__madbotAILoaders[key];
        }

        window.__madbotAILoaders[key] = new Promise((resolve, reject) => {
            const existing = document.querySelector(`script[data-madbot-ai="${key}"]`);

            if (existing) {
                existing.addEventListener('load', () => resolve());
                existing.addEventListener('error', () => reject(new Error(`failed to load ${key}`)));
                return;
            }

            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.crossOrigin = 'anonymous';
            script.setAttribute('data-madbot-ai', key);

            script.onload = () => resolve();
            script.onerror = () => reject(new Error(`failed to load ${src}`));

            document.head.appendChild(script);
        });

        return window.__madbotAILoaders[key];
    }

    _scheduleLoop () {
        if (!this.detectionOn) return;

        if (this.loopTimer) {
            clearTimeout(this.loopTimer);
        }

        this.loopTimer = setTimeout(() => {
            this._detectOnce();
        }, this.detectInterval);
    }

    async _detectOnce () {
        if (!this.detectionOn) return;

        if (this.loopBusy) {
            this._scheduleLoop();
            return;
        }

        this.loopBusy = true;

        try {
            if (!this.model) {
                await this._ensureModel();
            }

            const frame = await this._getDetectionFrame();

            if (!frame) {
                this.status = `no ${this.detectionSource} frame`;
                this.loopBusy = false;
                this._scheduleLoop();
                return;
            }

            const results = await this.model.detect(
                frame,
                this.maxBoxes,
                this.minScore
            );

            this.predictions = results.map(item => ({
                class: item.class,
                score: item.score,
                bbox: item.bbox
            }));

            this._drawOverlay();
            this.status = `detecting ${this.predictions.length} from ${this.detectionSource}`;
        } catch (error) {
            console.warn('MadBot Object AI detection error:', error);
            this.status = `detection error: ${error.message || error}`;
        }

        this.loopBusy = false;
        this._scheduleLoop();
    }

    async _getDetectionFrame () {
        if (this.detectionSource === 'stage') {
            return this._getStageFrame();
        }

        return this._getCameraFrame();
    }

    _getCameraFrame () {
        const videoDevice = this._videoDevice();

        if (!videoDevice || !videoDevice.videoReady) {
            return null;
        }

        return videoDevice.getFrame({
            format: Video.FORMAT_CANVAS,
            dimensions: [480, 360],
            mirror: this.mirror,
            cacheTimeout: this.detectInterval
        });
    }

    _getStageFrame () {
        const renderer = this.runtime && this.runtime.renderer;

        if (!renderer || !renderer.requestSnapshot) {
            return Promise.resolve(null);
        }

        if (!this.stageCanvas) {
            this.stageCanvas = document.createElement('canvas');
            this.stageCanvas.width = 480;
            this.stageCanvas.height = 360;
            this.stageCtx = this.stageCanvas.getContext('2d');
        }

        const overlayWasVisible = this.overlayDrawable !== -1;

        if (overlayWasVisible && renderer.updateDrawableVisible) {
            renderer.updateDrawableVisible(this.overlayDrawable, false);
        }

        return new Promise(resolve => {
            renderer.requestSnapshot(dataURI => {
                if (overlayWasVisible && renderer.updateDrawableVisible) {
                    renderer.updateDrawableVisible(this.overlayDrawable, true);
                }

                if (!dataURI) {
                    resolve(null);
                    return;
                }

                const img = new Image();

                img.onload = () => {
                    this.stageCtx.clearRect(0, 0, 480, 360);
                    this.stageCtx.drawImage(img, 0, 0, 480, 360);
                    resolve(this.stageCanvas);
                };

                img.onerror = () => {
                    resolve(null);
                };

                img.src = dataURI;
            }, 'image/png');
        });
    }

    _findPrediction (objectName) {
        const name = Cast.toString(objectName).trim().toLowerCase();

        if (!this.predictions.length) return null;

        if (!name || name === 'any') {
            return this.predictions[0];
        }

        return this.predictions.find(item =>
            item.class.toLowerCase() === name
        ) || null;
    }

    _ensureOverlay () {
        if (!this.overlayCanvas) {
            this.overlayCanvas = document.createElement('canvas');
            this.overlayCanvas.width = 480;
            this.overlayCanvas.height = 360;
            this.overlayCtx = this.overlayCanvas.getContext('2d');
        }

        const renderer = this.runtime && this.runtime.renderer;
        if (!renderer) return;

        if (this.overlaySkinId === -1) {
            this.overlaySkinId = renderer.createBitmapSkin(this.overlayCanvas, 1);
            this.overlayDrawable = renderer.createDrawable(StageLayering.SPRITE_LAYER);

            renderer.updateDrawableSkinId(this.overlayDrawable, this.overlaySkinId);
            renderer.updateDrawableVisible(this.overlayDrawable, true);

            if (renderer.updateDrawablePosition) {
                renderer.updateDrawablePosition(this.overlayDrawable, [0, 0]);
            }
        }
    }

    _drawOverlay () {
        this._ensureOverlay();

        if (!this.overlayCtx || !this.overlayCanvas) return;

        const ctx = this.overlayCtx;
        ctx.clearRect(0, 0, 480, 360);

        ctx.lineWidth = 3;
        ctx.font = '16px Arial';
        ctx.textBaseline = 'top';

        for (const item of this.predictions) {
            const box = item.bbox;
            const x = box[0];
            const y = box[1];
            const width = box[2];
            const height = box[3];

            if (this.showBox) {
                ctx.strokeStyle = '#F88A17';
                ctx.strokeRect(x, y, width, height);
            }

            if (this.showLabel || this.showScore) {
                let label = '';

                if (this.showLabel) {
                    label += item.class;
                }

                if (this.showScore) {
                    const score = Math.round(item.score * 100);
                    label += this.showLabel ? ` ${score}%` : `${score}%`;
                }

                const textWidth = ctx.measureText(label).width + 10;
                const labelY = Math.max(0, y - 24);

                ctx.fillStyle = 'rgba(248, 138, 23, 0.9)';
                ctx.fillRect(x, labelY, textWidth, 22);

                ctx.fillStyle = '#ffffff';
                ctx.fillText(label, x + 5, labelY + 3);
            }
        }

        const renderer = this.runtime && this.runtime.renderer;

        if (renderer && this.overlaySkinId !== -1) {
            renderer.updateBitmapSkin(this.overlaySkinId, this.overlayCanvas, 1);
            renderer.updateDrawableVisible(this.overlayDrawable, true);

            if (this.runtime.requestRedraw) {
                this.runtime.requestRedraw();
            }
        }
    }

    _clearOverlay () {
        if (this.overlayCtx && this.overlayCanvas) {
            this.overlayCtx.clearRect(0, 0, 480, 360);
        }

        const renderer = this.runtime && this.runtime.renderer;

        if (renderer && this.overlaySkinId !== -1) {
            renderer.updateBitmapSkin(this.overlaySkinId, this.overlayCanvas, 1);
            renderer.updateDrawableVisible(this.overlayDrawable, false);

            if (this.runtime.requestRedraw) {
                this.runtime.requestRedraw();
            }
        }
    }

    _clamp (value, min, max) {
        if (Number.isNaN(value)) return min;
        return Math.min(max, Math.max(min, value));
    }
}

module.exports = Scratch3MadBotObjectAI;